import { Pagination } from '@fortune-ui'
import { useApiECommerceOrders } from '@hooks-api/ec/useApiECommerceOrders'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { type WpOrderStatus, WP_ORDER_STATUS, WP_ORDER_TYPE } from '@hooks-api/types/dataModel/eCommerceOrders'
import { Box } from '@pets-ui/Box/Box'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { SnackbarProvider, SnackbarContext } from '@pets-ui/Snackbar/SnackbarProvider'
import { SnackbarVariants } from '@pets-ui/Snackbar/SnackbarVariants'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import { OrderEmptyCard } from '@wp-my-account/components/OrderEmptyCard'
import { Tabs } from '@wp-my-account/components/Tabs'
import wpMyAccountI18nInstance from '@wp-my-account/i18n/i18nConfig'
import queryString from 'query-string'
import { useState, useEffect, useRef, type ChangeEvent, type SyntheticEvent } from 'react'
import { I18nextProvider } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom-v5'
import { BrandCompactSelector } from '../../components/BrandCompactSelector/BrandCompactSelector'
import { useStmCampaignCode } from '../../hooks'
import { OrderList } from './components/OrderList'
import { OrderSearchInput } from './components/OrderSearchInput'
import { useOrderSearch } from './hooks/use-order-search'

const ORDER_TABS_DATA = [
  { label: wpMyAccountI18nInstance.t('orderTab.all'), value: WP_ORDER_STATUS.ALL },
  { label: wpMyAccountI18nInstance.t('orderTab.pending'), value: WP_ORDER_STATUS.PENDING },
  { label: wpMyAccountI18nInstance.t('orderTab.processing'), value: WP_ORDER_STATUS.PROCESSING },
  { label: wpMyAccountI18nInstance.t('orderTab.completed'), value: WP_ORDER_STATUS.COMPLETED },
  { label: wpMyAccountI18nInstance.t('orderTab.cancelled'), value: WP_ORDER_STATUS.CANCELLED },
]

interface OrdersProps {
  basePath: string
  onRepayClick?: () => void
  onCancelOrderClick?: () => void
}

export const Orders = ({ basePath, onRepayClick, onCancelOrderClick }: OrdersProps) => {
  const isWordPressMobile = useResponsive('wordpressMobile')
  const history = useHistory()
  const { search } = useLocation()
  const { campaignCode, campaignId, projectCode } = useStmCampaignCode()
  const { destructuredData: campaignActivity } = useApiCampaignActivityInfo(campaignCode)
  const siteId = campaignActivity?.site_id

  const defaultTabValue = queryString.parse(search).tabValue as WpOrderStatus || WP_ORDER_STATUS.ALL
  const [tabValue, setTabValue] = useState<WpOrderStatus>(defaultTabValue)
  const [page, setPage] = useState(1)
  const [focusedTransactionId, setFocusedTransactionId] = useState<string | null>(null)
  const hasFocusedRef = useRef(false)
  const prevCampaignCodeRef = useRef(campaignCode)

  const {
    searchParams,
    searchKeyword,
    searchField,
    setSearchKeyword,
    changeField,
    submitSearch,
    resetSearch,
    clearKeyword,
  } = useOrderSearch()

  const handleSearch = () => {
    submitSearch()
    setPage(1)
  }

  useEffect(() => {
    if (prevCampaignCodeRef.current !== campaignCode) {
      prevCampaignCodeRef.current = campaignCode
      setTabValue(WP_ORDER_STATUS.ALL)
      setPage(1)
      setFocusedTransactionId(null)
      hasFocusedRef.current = false
      resetSearch()
      history.replace({ search: `?tabValue=${WP_ORDER_STATUS.ALL}` })
    }
  }, [campaignCode, history, resetSearch])

  const handleChangeTab = (_event: SyntheticEvent, newValue: WpOrderStatus) => {
    setPage(1)
    setFocusedTransactionId(null)
    hasFocusedRef.current = false
    resetSearch()
    history.replace({ search: `?tabValue=${newValue}` })
  }

  const handleChangePage = (_event: ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const { destructuredData: eCommerceOrders, isLoading } = useApiECommerceOrders(
    siteId && projectCode
      ? {
        project_code: projectCode,
        site_id: siteId,
        status: tabValue,
        type: WP_ORDER_TYPE.PAYMENT_TRANSACTION,
        page: page - 1,
        campaign_id: campaignId ?? undefined,
        ...searchParams,
      }
      : undefined,
  )

  useEffect(() => {
    const tabFromUrl = queryString.parse(search).tabValue as WpOrderStatus || WP_ORDER_STATUS.ALL
    setTabValue(tabFromUrl)
    setPage(1)
    // NOTE:(取消訂單後focus到該筆訂單) URL 參數帶入 focusedTransactionId，觸發 useEffect 自動換頁並 focus 到該筆訂單
    const focusedOrderFromUrl = (queryString.parse(search).focusedTransactionId as string) || null
    if (focusedOrderFromUrl) setFocusedTransactionId(focusedOrderFromUrl)
  }, [search])

  // NOTE:(取消訂單後focus到該筆訂單) state 有值後立即清除 URL param（避免重整後重新 focus）
  useEffect(() => {
    if (!focusedTransactionId) return
    if (!queryString.parse(search).focusedTransactionId) return // URL 已清除，避免重複觸發
    const { focusedTransactionId: _, ...rest } = queryString.parse(search)
    history.replace({ search: queryString.stringify(rest) })
  }, [focusedTransactionId, search, history])

  // NOTE:(取消訂單後focus到該筆訂單) focusedTransactionId 有值時，自動換頁直到找到目標訂單
  useEffect(function autoPageToFocusedOrder() {
    if (!focusedTransactionId || isLoading || !eCommerceOrders) return
    const isOnCurrentPage = eCommerceOrders.orders.some(
      order => order.payment_transaction_id === focusedTransactionId,
    )
    if (isOnCurrentPage) {
      hasFocusedRef.current = true // 已找到，停止自動換頁
      return
    }
    if (!hasFocusedRef.current && page < (eCommerceOrders.total_pages ?? 1)) {
      setPage(prev => prev + 1)
    }
  }, [focusedTransactionId, eCommerceOrders, isLoading, page])

  return (
    <SnackbarProvider>
      <SnackbarContext.Consumer>
        {(value) => {
          const { handleExited, snackbarProps, closeSnackbar } = value || {}
          return (
            <SnackbarVariants
              {...snackbarProps}
              autoHideDuration={3000}
              onClose={closeSnackbar}
              onExited={handleExited}
            />
          )
        }}
      </SnackbarContext.Consumer>
      <I18nextProvider i18n={wpMyAccountI18nInstance}>
        <VStack width="100%" gap={4}>
          <BrandCompactSelector />
          <Tabs
            color={colors.primary600}
            variant={isWordPressMobile ? 'scrollable' : 'fullWidth'}
            tabData={ORDER_TABS_DATA}
            value={tabValue}
            onChange={handleChangeTab}
          />
          <OrderSearchInput
            searchKeyword={searchKeyword}
            searchField={searchField}
            onKeywordChange={setSearchKeyword}
            onFieldChange={changeField}
            onSearch={handleSearch}
            onClearKeyword={clearKeyword}
          />
          {isLoading
            ? <OrderEmptyCard><CircularProgress fontSize={20} color={colors.grey300} /></OrderEmptyCard>
            : eCommerceOrders?.orders?.length === 0
              ? <OrderEmptyCard>{wpMyAccountI18nInstance.t('wpOrder.noOrder')}</OrderEmptyCard>
              : (
                <VStack gap={4} alignItems="center">
                  <Box width="100%">
                    <OrderList
                      list={eCommerceOrders?.orders || []}
                      status={tabValue}
                      basePath={basePath}
                      onRepayClick={onRepayClick}
                      onCancelOrderClick={onCancelOrderClick}
                      focusedTransactionId={focusedTransactionId}
                    />
                  </Box>
                  <Pagination
                    sx={{ justifyContent: 'center' }}
                    variant="outlined"
                    count={eCommerceOrders?.total_pages || 1}
                    page={page}
                    onChange={handleChangePage}
                  />
                </VStack>
              )}
        </VStack>
      </I18nextProvider>
    </SnackbarProvider>
  )
}
